import { useMutation, useQuery } from "@apollo/react-hooks";
import { Form, InputNumber, Spin, notification } from "antd";
import React from "react";
import { SET_VARIABLE } from "../../../graphql/mutations";
import { GET_VARIABLES } from "../../../graphql/queries";
import { handleError } from "../../../helper";

const FIELDS = [
  {
    name: "overdue_awaiting_buyer_confirm",
    label: "Awaiting Buyer Confirm overdue after (hours)",
    defaultHours: 24,
  },
  {
    name: "overdue_rejected",
    label: "Rejected overdue after (hours)",
    defaultHours: 15,
  },
];

// Keep values within the duration range supported by the refresh jobs.
const MAX_HOURS = 2562047;
const validHours = (value) =>
  typeof value === "number" && Number.isFinite(value) && value > 0 && value <= MAX_HOURS;

export default React.forwardRef(function TaskOverdueSettings({ teamId, setLoading }, ref) {
  const [form] = Form.useForm();
  const changed = React.useRef(false);
  const saving = React.useRef(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const { data, loading, error } = useQuery(GET_VARIABLES, {
    variables: { filter: { search: `${teamId}__overdue_` } },
    fetchPolicy: "network-only",
    skip: !teamId,
  });
  const [setVariable] = useMutation(SET_VARIABLE);

  React.useEffect(() => {
    if (!data || changed.current) return;
    const values = {};
    for (const field of FIELDS) {
      const record = data.variables?.find(({ key }) => key === `${teamId}__${field.name}`);
      const hours = Number(record?.value);
      values[field.name] = validHours(hours) ? hours : field.defaultHours;
    }
    form.setFieldsValue(values);
  }, [data, form, teamId]);

  const handleFinish = React.useCallback(async (values) => {
    if (!changed.current || saving.current || loading || error || !teamId) return;
    saving.current = true;
    setIsSaving(true);
    setLoading(true);
    try {
      const results = await Promise.allSettled(FIELDS.map(({ name }) => setVariable({
        variables: { input: { key: `${teamId}__${name}`, value: String(values[name]) } },
      })));
      const failed = results.find((result) => result.status === "rejected");
      if (failed) throw failed.reason;
      changed.current = false;
      notification.success({ message: "Task overdue settings saved." });
    } catch (err) {
      notification.error({ message: handleError(err?.toString()) });
    } finally {
      saving.current = false;
      setIsSaving(false);
      setLoading(false);
    }
  }, [error, loading, setLoading, setVariable, teamId]);

  React.useImperativeHandle(ref, () => ({ onSubmit: form.submit }), [form]);

  if (loading) return <Spin />;
  if (error) return <div>Error: {handleError(error.toString())}</div>;

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onValuesChange={() => { changed.current = true; }}
      initialValues={Object.fromEntries(FIELDS.map(({ name, defaultHours }) => [name, defaultHours]))}
    >
      {FIELDS.map(({ name, label, defaultHours }) => (
        <Form.Item
          key={name}
          name={name}
          label={label}
          extra={`Default: ${defaultHours} hours. Changes apply on the next overdue refresh.`}
          rules={[{
            validator: (_, value) => validHours(value)
              ? Promise.resolve()
              : Promise.reject(new Error(`Enter a number greater than 0 and no more than ${MAX_HOURS} hours.`)),
          }]}
        >
          <InputNumber step={0.5} max={MAX_HOURS} disabled={isSaving} style={{ width: "100%" }} />
        </Form.Item>
      ))}
    </Form>
  );
});
